import { useEffect, useMemo, useState } from 'react';

import { CalculatorCalendar } from './calculator-calendar';
import { CalculatorLegend } from './calculator-legend';
import { formatCivilDate, instantToCivilUtc } from './civil-date';
import {
  resolveProceduralDate,
  type ResolveProceduralDateResult,
} from '../../../../../backend/src/matter/procedural-timeline/date-resolution';
import { useProceduralHolidays } from '@/api/queries/useProceduralHolidays';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { COUNTING_OPTIONS, CountingKey } from '@/new/stores/timeline-calculator-store';

interface EmbeddedCalculatorProps {
  matterId: string;
  sourceDate: Date | null;
  days: number;
  /** Live counting key from the parent form. */
  counting: CountingKey;
  /** Called when the user accepts a date — should set the form's start date and close. */
  onApply: (date: Date) => void;
}

/**
 * Embedded calculator used inside a modal.
 */
export function EmbeddedCalculator({ matterId, sourceDate, days, counting, onApply }: EmbeddedCalculatorProps) {
  const normalizedSource = useMemo(() => instantToCivilUtc(sourceDate), [sourceDate]);
  const option = useMemo(() => COUNTING_OPTIONS.find((o) => o.key === counting) ?? COUNTING_OPTIONS[0], [counting]);

  const yearRange = useMemo(() => {
    const anchorYear = normalizedSource?.getUTCFullYear() ?? new Date().getUTCFullYear();
    return { fromYear: anchorYear - 1, toYear: anchorYear + 2 };
  }, [normalizedSource]);

  const { data: holidays } = useProceduralHolidays({
    matterId,
    jurisdiction: option.jurisdiction,
    fromYear: yearRange.fromYear,
    toYear: yearRange.toYear,
  });

  const computation = useMemo<{ result: ResolveProceduralDateResult | null; error: string | null }>(() => {
    if (normalizedSource === null) return { result: null, error: "The linked event has no date — can't calculate." };
    if (!Number.isFinite(days) || days === 0) return { result: null, error: 'Enter a non-zero number of days.' };
    if (holidays === undefined) return { result: null, error: null };
    try {
      const r = resolveProceduralDate({
        startDate: normalizedSource,
        interval: { days },
        jurisdiction: option.jurisdiction,
        dayCount: option.dayCount,
        holidays,
      });
      return { result: r, error: null };
    } catch (err) {
      return { result: null, error: err instanceof Error ? err.message : 'Calculation failed.' };
    }
  }, [normalizedSource, days, option, holidays]);

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  useEffect(() => {
    if (computation.result !== null) setSelectedDate(computation.result.resultDate);
  }, [computation.result]);

  if (computation.error !== null) {
    return (
      <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
        {computation.error}
      </p>
    );
  }

  if (computation.result === null || normalizedSource === null) {
    return <p className="text-xs text-gray-500">Loading…</p>;
  }

  const r = computation.result;
  const picked = selectedDate ?? r.resultDate;

  return (
    <div className="flex flex-col gap-3">
      <CalculatorLegend result={r} />
      <CalculatorCalendar
        result={r}
        startDate={normalizedSource}
        selectedDate={picked}
        onSelectDate={(d) => setSelectedDate(d)}
      />

      <div className="flex flex-col gap-1 text-xs text-gray-700">
        {r.startDateExcluded && (
          <p>
            <span className="text-gray-400">↳ </span>
            <strong>Day 0, {formatCivilDate(normalizedSource, 'EEEE d MMMM yyyy')}</strong>: start day, not counted.
          </p>
        )}
        <p>
          <span className="text-gray-400">↳ </span>
          Counted {r.countedDates.length} {labelForCounting(r.dayCount, r.countedDates.length)} between the linked date
          and the result.
        </p>
        {r.endDateRolledForward && (
          <p>
            <span className="text-gray-400">↳ </span>
            Result rolled forward to the next business day.
          </p>
        )}
        <p className="text-teal-700">
          <span className="text-gray-400">↳ </span>
          <strong>Result: {formatCivilDate(r.resultDate, 'EEEE d MMMM yyyy')}.</strong>
        </p>
        {r.warnings.map((w) => (
          <p key={w} className="text-amber-700">
            <span className="text-amber-500">↳ </span>
            {w}
          </p>
        ))}
      </div>

      <div className="flex items-center justify-end">
        <Button type="button" variant="primary" onClick={() => onApply(picked)}>
          <Icon className="lucide--check" />
          Use {formatCivilDate(picked, 'd MMM yyyy')}
        </Button>
      </div>
    </div>
  );
}

function labelForCounting(dayCount: ResolveProceduralDateResult['dayCount'], count: number): string {
  const noun = Math.abs(count) === 1 ? 'day' : 'days';
  switch (dayCount) {
    case 'calendar':
      return `calendar ${noun}`;
    case 'business':
      return `business ${noun}`;
    case 'clear-cpr':
      return `clear ${noun}`;
  }
}
